import { useTheme } from "@emotion/react";
import {
  Table,
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  useMediaQuery,
} from "@chakra-ui/react";
import { getRgbaColor } from "@mtb/mtb-style";
import { LuArrowRight } from 'react-icons/lu';

interface DataColumn {
  title: string;
  fieldName: string;
}

interface DataRow {
  [key: string]: any;
}

interface DataTableProps {
  columns: DataColumn[];
  data: DataRow[];
  selectedRowKey: string | null;
  onRowSelect: (row: DataRow) => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  selectedRowKey,
  onRowSelect,
}) => {
  const [isVerySmallMediaScreen] = useMediaQuery(["(max-width: 547px)"]);

  const theme = useTheme();
  const primaryColor = theme.colors.primary;

  // Gradient background color
  const gradientBg = `linear-gradient(135deg, ${getRgbaColor(
    primaryColor,
    0.2
  )}, white)`;

  const textColor = "gray.600"; // Define a lighter gray color for text

  if (isVerySmallMediaScreen) {
    return (
      <VStack gap={4} align="stretch">
        {data.map((row, index) => (
          <Box
            key={index}
            p={4}
            borderRadius="md"
            bg={row.key === selectedRowKey ? gradientBg : "white"}
            boxShadow="md"
          >
            <VStack align="stretch" gap={2}>
              {columns.map((column) => (
                <HStack key={column.fieldName} justify="space-between">
                  <Text color={textColor}>{column.title}:</Text>
                  <Text>{row[column.fieldName]}</Text>
                </HStack>
              ))}
              <HStack justify="flex-end">
                <IconButton
                  aria-label="Select Tenant"
                  onClick={() => onRowSelect(row)}
                  disabled={row.key === selectedRowKey}
                  variant="outline"
                  colorPalette={row.key === selectedRowKey ? "teal" : "gray"}><LuArrowRight /></IconButton>
              </HStack>
            </VStack>
          </Box>
        ))}
      </VStack>
    );
  }

  return (
    <Box overflowX="auto" width="100%">
      <Table.Root variant="line" size="sm">
        <Table.Header>
          <Table.Row>
            {columns.map((column) => (
              <Table.ColumnHeader key={column.fieldName}>{column.title}</Table.ColumnHeader>
            ))}
            <Table.ColumnHeader />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((row, index) => (
            <Table.Row
              key={index}
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              bg={row.key === selectedRowKey ? gradientBg : "white"}
            >
              {columns.map((column) => (
                <Table.Cell key={column.fieldName} color={textColor}>
                  {row[column.fieldName]}
                </Table.Cell>
              ))}
              <Table.Cell textAlign="right">
                <IconButton
                  aria-label="Select Tenant"
                  onClick={() => onRowSelect(row)}
                  disabled={row.key === selectedRowKey}
                  variant="outline"
                  colorPalette={row.key === selectedRowKey ? "teal" : "gray"}><LuArrowRight /></IconButton>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};
